import { useCallback, useMemo, type ReactNode } from 'react';
import { ActionButton } from '../action-button/ActionButton';
import { ModalHeader } from '../modal-header/ModalHeader';
import { createTrackEvent } from '../../clients/analytics';
import { InteractiveSpotEvent } from '../../types/analytics';
import type { BookSpotSuccessData } from './BookSpot.types';

type BookSpotSuccessProps = {
  data: BookSpotSuccessData;
};

type ModalResponseProps = {
  onClose: () => void;
  iconClassName: string;
  heading?: string;
  buttonId: string;
  buttonLabel?: string;
  onButtonClick: () => void;
  children?: ReactNode;
};

function ModalResponse({
  onClose,
  iconClassName,
  heading,
  buttonId,
  buttonLabel,
  onButtonClick,
  children,
}: ModalResponseProps) {
  return (
    <>
      <ModalHeader data={{ onClose, dismissModal: false }} />
      <div className="modal-body modal-bookbuy-response">
        <div className="row">
          <div className="col s12">
            <div className="content-group">
              <div className={iconClassName}></div>
            </div>
            <div className="content-group">
              <h4>{heading}</h4>
              <h3>{children}</h3>
            </div>
            <div className="content-group modal-book-button">
              <ActionButton data={{ id: buttonId, label: buttonLabel, onClick: onButtonClick }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function BookSpotSuccessContent({ data }: BookSpotSuccessProps) {
  const {
    eventName,
    successLabel,
    doneLabel,
    timeSlotId,
    bookingId,
    userId,
    onDone,
    trackEvent,
  } = data;

  const track = useMemo(() => createTrackEvent(trackEvent), [trackEvent]);

  const handleDone = useCallback(() => {
    track({
      name: InteractiveSpotEvent.MODAL_ACKNOWLEDGED_SUCCESS,
      category: 'source:event',
      metadata: { timeSlotId, bookingId, userId },
    });
    onDone();
  }, [track, timeSlotId, bookingId, userId, onDone]);

  return (
    <ModalResponse
      onClose={handleDone}
      iconClassName="icon-success icon-confirmation"
      heading={successLabel}
      buttonId="book-spot-done"
      buttonLabel={doneLabel}
      onButtonClick={handleDone}
    >
      {eventName}
    </ModalResponse>
  );
}

export function BookSpotSuccess({ data }: BookSpotSuccessProps) {
  if (!data) {
    return null;
  }

  return <BookSpotSuccessContent data={data} />;
}
